import { useEffect, useRef, useState } from 'react';
import {
  DeviceInfo,
  WifiNetwork,
  WifiRequestTicket,
  getDeviceInfo,
  requestWifiConnect,
  requestWifiEnabled,
  requestWifiScan,
} from '../../services/admin';
import type { WifiConfirm, WifiControlsViewProps, WifiPending } from './WifiControls';
import { ageText, operationSucceeded, wifiResultState, type WifiResultKind } from './wifiNetworkText';

// State and actions behind WifiControlsView (documentation/protocol.md "Wi-Fi remote control").

const resultMessage = (kind: WifiResultKind, info: DeviceInfo): string => {
  if (kind === 'scan') {
    if (info.wifi_scan_status !== 'COMPLETED') return 'Scan failed: the radio could not produce fresh results (Wi-Fi off, or Android limited scanning). Try again shortly.';
    let n = 0;
    try { n = (JSON.parse(info.wifi_scan_result || '[]') as unknown[]).length; } catch { /* unreadable */ }
    return n === 0 ? 'Scan complete: no networks found.' : `Scan complete: ${n} network${n === 1 ? '' : 's'}.`;
  }
  const message = info.wifi_operation_message || '';
  return operationSucceeded(info) ? message || 'Done.' : `Failed${message ? `: ${message}` : '.'}`;
};

const parseNetworks = (raw: string | null | undefined): WifiNetwork[] => {
  if (!raw) return [];
  try { return JSON.parse(raw) as WifiNetwork[]; } catch { return []; }
};

/**
 * State and actions for one radio's Wi-Fi controls. onInfo receives every fresh Device Info read
 * (Device Details uses it to refresh its own fields).
 */
export function useWifiControls(radioId: string, online: boolean, onInfo?: (info: DeviceInfo) => void, external?: DeviceInfo | null) {
  const [info, setInfo] = useState<DeviceInfo | null>(null);
  const [pending, setPending] = useState<WifiPending>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [selected, setSelected] = useState<WifiNetwork | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState<WifiConfirm | null>(null);
  const [now, setNow] = useState(() => Date.now());
  // Bumped when the radio changes or the component goes away: a poll of an earlier generation never
  // updates anything (no other radio's data, no update after navigation).
  const generation = useRef(0);
  const onInfoRef = useRef(onInfo);
  onInfoRef.current = onInfo;

  const apply = (fresh: DeviceInfo, gen = generation.current) => {
    if (gen !== generation.current) return;
    setInfo(fresh);
    setNow(Date.now());
    onInfoRef.current?.(fresh);
  };

  const pageReadsInfo = useRef(external !== undefined);
  useEffect(() => {
    const counter = generation; // the counter object itself, not a DOM node
    const gen = ++counter.current;
    if (!pageReadsInfo.current) getDeviceInfo(radioId).then((fresh) => apply(fresh, gen)).catch(() => undefined);
    return () => {
      counter.current++;
      setPassword(''); // never outlive the component
    };
  }, [radioId]);

  // A page that reads Device Info itself (Device Details) keeps these controls current.
  useEffect(() => {
    if (external) setInfo(external);
  }, [external]);

  const waitFor = (kind: WifiResultKind, ticket: WifiRequestTicket) => {
    const deadline = Date.now() + 90_000;
    const gen = generation.current;
    const tick = async () => {
      if (gen !== generation.current) return;
      const fresh = await getDeviceInfo(radioId).catch(() => null);
      if (gen !== generation.current) return;
      if (fresh) {
        apply(fresh, gen);
        const state = wifiResultState(kind, fresh, ticket);
        if (state === 'superseded') {
          setPending(null);
          setMessage('Another Wi-Fi request for this radio replaced this one.');
          return;
        }
        if (state === 'done') {
          setPending(null);
          setMessage(resultMessage(kind, fresh));
          return;
        }
      }
      if (Date.now() > deadline) {
        setPending(null);
        setMessage('No answer from the radio yet. Its app may be an older version or it may have lost its connection.');
        return;
      }
      setTimeout(tick, 3_000);
    };
    setTimeout(tick, 2_000);
  };

  const send = async (kind: WifiPending, request: () => Promise<WifiRequestTicket>, resultKind: WifiResultKind, progress: string) => {
    setPending(kind);
    setMessage(progress);
    try {
      waitFor(resultKind, await request());
    } catch (err) {
      setPending(null);
      setMessage(err instanceof Error ? err.message : 'Request failed');
      const gen = generation.current;
      getDeviceInfo(radioId).then((fresh) => apply(fresh, gen)).catch(() => undefined);
    }
  };

  const enabled = info?.wifi_enabled ?? null;
  const viewProps: WifiControlsViewProps = {
    online,
    backgroundCommands: info ? !!info.wifi_capabilities?.background_commands : null,
    enabled,
    ssid: info?.wifi_ssid ?? null,
    signalDbm: info?.wifi_signal_dbm ?? null,
    linkSpeedMbps: info?.wifi_link_speed_mbps ?? null,
    pending,
    message,
    networks: parseNetworks(info?.wifi_scan_result),
    scannedAgo: info?.wifi_scan_status === 'COMPLETED' ? ageText(info?.wifi_scan_updated_at, now) : null,
    selected,
    password,
    confirm,
    onToggle: () => {
      if (enabled) setConfirm({ kind: 'disable' });
      else send('enabled', () => requestWifiEnabled(radioId, true), 'operation', 'Enabling Wi-Fi…');
    },
    onScan: () => send('scan', () => requestWifiScan(radioId), 'scan', 'Scanning Wi-Fi networks…'),
    onSelect: (network) => {
      setSelected(network);
      setPassword('');
      setConfirm(null);
    },
    onPassword: setPassword,
    onConnect: () => { if (selected) setConfirm({ kind: 'connect', network: selected }); },
    onCancelConfirm: () => setConfirm(null),
    onConfirm: () => {
      const c = confirm;
      setConfirm(null);
      if (c?.kind === 'disable') {
        send('enabled', () => requestWifiEnabled(radioId, false), 'operation', 'Disabling Wi-Fi…');
      } else if (c?.kind === 'connect') {
        const secret = password;
        // Cleared before the request even starts: success or failure, it is never kept.
        setPassword('');
        setSelected(null);
        send('connect', () => requestWifiConnect(radioId, c.network.ssid, secret), 'operation', `Connecting to ${c.network.ssid}…`);
      }
    },
  };
  return viewProps;
}
